import { useContext } from 'react';
import { useDispatch } from 'react-redux';

import {
  type ContractDataType,
  type PaidSpecContractType,
  type SpecContractStatusType,
} from '@/common/types/contract';
import { getStatusBySpecContractData } from '@/components/Contract/lib/getStusByContractData';
import { ModalContext } from '@/components/Modals/Modal/config';
import { addFullscreenModal } from '@/redux/reducers/modalsReducer';
import {
  selectSpecContractIsFail,
  selectSpecContractIsOpenByKind,
  selectSpecContractOpenDateByKind,
  selectSpecContractTermsByKind,
} from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import {
  getSpecContractModalToOpen,
  specContractRequirenments,
} from '../config/contract';
import { paidSpecContractList } from '../constants/contract';
import { playSound } from '../lib/soundEffect';
import { type ContractData, type SpecContractType } from '../types/contract';

import { useExpireContractTimer } from './contract/expire-contract-time';
import { useCompletionContractCheck } from './completion-contract-check';
import { useOpenSpecContractTimer } from './open-spec-contract-timer';

const useSpecContractTerms = (
  kind: SpecContractType | PaidSpecContractType,
): {
  data?: ContractData;
  isOpen: boolean;
  hasContractComplete: boolean;
  hasContractFail: boolean;
  openDate?: string;
} => {
  const data = useAppSelector((state) =>
    selectSpecContractTermsByKind(state, kind),
  );
  const isOpen = useAppSelector((state) =>
    selectSpecContractIsOpenByKind(state, kind),
  );
  const hasContractComplete = useCompletionContractCheck(data);
  const hasContractFail = useAppSelector((state) =>
    selectSpecContractIsFail(state, kind),
  );
  const openDate = useAppSelector((state) =>
    selectSpecContractOpenDateByKind(state, kind),
  );

  return {
    data,
    isOpen,
    hasContractComplete: hasContractComplete && isOpen,
    hasContractFail,
    openDate,
  };
};

const useSpecContractItem = (
  contract: ContractDataType,
): {
  kind: SpecContractType | PaidSpecContractType;
  status?: SpecContractStatusType;
  requairedLevel: number;
  timeLeft: number | null;
  contractTimerExtensionDuration: number | null;
  point: number;
  onClick?: () => void;
  hasContractComplete: boolean;
} => {
  const { openModal, closeModal } = useContext(ModalContext);
  const hasContractComplete = useCompletionContractCheck(contract?.data);
  const kind = contract.type as SpecContractType | PaidSpecContractType;
  const dispatch = useDispatch();

  const timerToOpenStatusEnd = useOpenSpecContractTimer(kind, closeModal);
  const timerToExpireStatusEnd = useExpireContractTimer(kind);
  const status = getStatusBySpecContractData(contract);

  const requairedLevel = specContractRequirenments[kind].level;

  const generateHandleClick = (
    kind: SpecContractType | PaidSpecContractType,
    status: SpecContractStatusType | undefined,
  ): (() => void) | undefined => {
    if (!status) {
      return;
    }

    const isPaidContract = paidSpecContractList.includes(
      kind as PaidSpecContractType,
    );

    if (isPaidContract && contract.isOpened === null) {
      return (): void => {
        playSound('buttons/proceed');
        dispatch(
          addFullscreenModal({
            name: 'buyPlusContract',
            props: { contractId: contract.id },
          }),
        );
      };
    } else {
      return (): void => {
        playSound('buttons/proceed');
        openModal(getSpecContractModalToOpen(kind, status, contract));
      };
    }
  };

  return {
    kind,
    status,
    requairedLevel,
    timeLeft: timerToOpenStatusEnd,
    contractTimerExtensionDuration: timerToExpireStatusEnd,
    point: contract?.point || 0,
    hasContractComplete,
    onClick: generateHandleClick(
      contract.type as SpecContractType | PaidSpecContractType,
      status,
    ),
  };
};

export { useSpecContractItem, useSpecContractTerms };
